import React from 'react';
import {
  Drawer,
  Box,
  Stack,
  Typography,
  Chip,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Paper,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect } from 'react';
import SendIcon from '@mui/icons-material/Send';
import DoneIcon from '@mui/icons-material/Done';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { fundingLabel, DEFAULT_FUNDING_TYPE, trainingStatusLabel, trainingTypeLabel, participantStatusLabel, inviteStatusLabel } from '../../constants/trainings';

const statusStyles = {
  Planned: { bg: 'rgba(124,58,237,0.12)', color: '#256878' },
  'In progress': { bg: 'rgba(59,130,246,0.12)', color: '#2563EB' },
  Completed: { bg: 'rgba(16,185,129,0.12)', color: '#3E8FA3' },
  Cancelled: { bg: 'rgba(148,163,184,0.2)', color: '#475569' },
};

const participantStatusStyles = {
  'Not started': { bg: 'rgba(148,163,184,0.2)', color: '#475569' },
  'In progress': { bg: 'rgba(59,130,246,0.12)', color: '#2563EB' },
  Completed: { bg: 'rgba(16,185,129,0.12)', color: '#3E8FA3' },
  Failed: { bg: 'rgba(248,113,113,0.15)', color: '#DC2626' },
};

// Mobile Participant Card Component
function MobileParticipantCard({ 
  participant, 
  training, 
  onParticipantComplete, 
  onEditParticipant, 
  onSendInvite, 
  inviteSending,
  onAcceptInvite 
}) {
  return (
    <Card 
      elevation={0} 
      sx={{ 
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        mb: 1.5,
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack spacing={1.5}>
          {/* Header */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box flex={1}>
              <Typography variant="subtitle2" fontWeight={700}>{participant.name}</Typography>
              <Typography variant="caption" color="text.secondary">{participant.email || 'Email mungon'}</Typography>
            </Box>
            <Chip
              label={participantStatusLabel(participant.status)}
              size="small"
              sx={{
                backgroundColor: (participantStatusStyles[participant.status] || participantStatusStyles['Not started']).bg,
                color: (participantStatusStyles[participant.status] || participantStatusStyles['Not started']).color,
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 22,
              }}
            />
          </Stack>

          <Divider />

          {/* Details */}
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Box>
              <Typography variant="caption" color="text.secondary">Departamenti</Typography>
              <Typography variant="body2" fontWeight={600}>{participant.department}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Ftesa</Typography>
              <Chip
                icon={participant.inviteStatus === 'Accepted' ? <DoneIcon fontSize="small" /> : undefined}
                label={inviteStatusLabel(participant.inviteStatus || 'Pending')}
                size="small"
                sx={{
                  backgroundColor:
                    participant.inviteStatus === 'Accepted'
                      ? 'rgba(16,185,129,0.12)'
                      : participant.inviteStatus === 'Sent'
                      ? 'rgba(59,130,246,0.12)'
                      : 'rgba(248,180,0,0.16)',
                  color:
                    participant.inviteStatus === 'Accepted'
                      ? '#3E8FA3'
                      : participant.inviteStatus === 'Sent'
                      ? '#2563EB'
                      : '#B45309',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  height: 22,
                  mt: 0.5,
                }}
              />
            </Box>
            {participant.completionDate && (
              <Box>
                <Typography variant="caption" color="text.secondary">Përfunduar</Typography>
                <Typography variant="body2" fontWeight={600}>{participant.completionDate}</Typography>
              </Box>
            )}
            {participant.score && (
              <Box>
                <Typography variant="caption" color="text.secondary">Rezultati</Typography>
                <Typography variant="body2" fontWeight={600} color="success.main">{participant.score}%</Typography>
              </Box>
            )}
          </Stack>

          {/* Actions */}
          <Stack spacing={1}>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditIcon sx={{ fontSize: '1rem' }} />}
                onClick={() => onEditParticipant?.(training.id, participant)}
                sx={{ fontSize: '0.8rem', flex: 1 }}
              >
                Edito
              </Button>
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon sx={{ fontSize: '1rem' }} />}
                onClick={() => onParticipantComplete(training.id, participant.id)}
                sx={{ fontSize: '0.8rem', flex: 1 }}
              >
                Përfunduar
              </Button>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<SendIcon sx={{ fontSize: '1rem' }} />}
                onClick={() => onSendInvite?.(training.id, participant.id)}
                disabled={
                  inviteSending?.trainingId === training.id &&
                  inviteSending?.participantId === participant.id
                }
                sx={{ fontSize: '0.8rem', flex: 1 }}
              >
                {participant.inviteStatus === 'Sent' ? 'Ridërgo' : 'Dërgo ftesë'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => onAcceptInvite?.(training.id, participant.id)}
                disabled={participant.inviteStatus === 'Accepted'}
                sx={{ fontSize: '0.8rem', flex: 1 }}
              >
                Prano
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function TrainingDetailsDrawer({
  open,
  training,
  onClose,
  onParticipantComplete,
  onNotify,
  onAddParticipant,
  onEditParticipant,
  onSendInvite,
  inviteSending,
  onAcceptInvite,
  onEditTraining = null,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (!open && typeof document !== 'undefined') {
      const active = document.activeElement;
      if (active && typeof active.blur === 'function') {
        active.blur();
      }
    }
  }, [open]);

  if (!training) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : isTablet ? '85%' : '70%',
          maxWidth: isMobile ? '100vw' : 1200,
          /**
           * Shiriti i sipërm i aplikacionit rri mbi panelin (z-index 1201 kundër
           * 1200), ndaj 61px e para të përmbajtjes zhdukeshin nën të: titulli i
           * trajnimit dilte i prerë përgjysmë, dhe butoni i mbylljes bashkë me
           * «Ndrysho» nuk shiheshin fare — paneli mbyllej vetëm duke klikuar
           * jashtë tij. Paneli nis poshtë shiritit, jo nën të.
           */
          top: { xs: 56, sm: 64 },
          height: { xs: 'calc(100% - 56px)', sm: 'calc(100% - 64px)' },
        },
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 1200,
          mx: 'auto',
          p: { xs: 2, sm: 2.5, md: 3, lg: 4 },
          height: '100%',
          overflowY: 'auto',
        }}
      >
        {/* Header */}
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          justifyContent="space-between" 
          alignItems={{ xs: 'flex-start', sm: 'flex-start' }} 
          spacing={{ xs: 1.5, sm: 2 }}
          mb={{ xs: 2, md: 2.5 }}
        >
          <Box flex={1}>
            <Typography variant="overline" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
              Detajet e trajnimit
            </Typography>
            <Typography 
              variant="h5" 
              fontWeight={700}
              sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' } }}
            >
              {training.name}
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                display: { xs: 'none', sm: 'block' },
                fontSize: { sm: '0.875rem', md: '0.875rem' }
              }}
            >
              {training.description}
            </Typography>
            <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" gap={0.5}>
              <Chip
                label={trainingStatusLabel(training.status)}
                size="small"
                sx={{
                  backgroundColor: (statusStyles[training.status] || statusStyles.Planned).bg,
                  color: (statusStyles[training.status] || statusStyles.Planned).color,
                  fontWeight: 700,
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  height: { xs: 22, sm: 24 },
                }}
              />
              <Chip 
                label={trainingTypeLabel(training.type)} 
                size="small"
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  height: { xs: 22, sm: 24 },
                }}
              />
              {/* Financimi: kodi nga baza, etiketa nga fjalori. */}
              <Chip
                label={fundingLabel(training.fundingType || DEFAULT_FUNDING_TYPE)}
                size="small"
                variant="outlined"
                data-testid="training-funding-chip"
                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, height: { xs: 22, sm: 24 }, borderColor: 'primary.main', color: 'primary.main' }}
              />
            </Stack>
          </Box>
          
          <Stack direction="row" spacing={1} alignItems="center">
            {/* «Ndrysho» rrinte në rreshtin e çipave, mes statusit dhe llojit —
                një veprim i veshur si etiketë. Veprimet e panelit rrinë bashkë. */}
            {onEditTraining && (
              <Button size="small" variant="outlined" onClick={() => onEditTraining(training)}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}>
                Ndrysho
              </Button>
            )}
            {!isMobile && (
              <Button
                variant="text"
                startIcon={<ArrowBackIosNewIcon fontSize="small" />}
                onClick={onClose}
                sx={{ 
                  textTransform: 'none', 
                  fontWeight: 600,
                  display: { xs: 'none', sm: 'inline-flex' },
                  fontSize: { sm: '0.875rem' }
                }}
              >
                Kthehu
              </Button>
            )}
            <IconButton onClick={onClose} size="small">
              <CloseIcon fontSize={isMobile ? 'medium' : 'small'} />
            </IconButton>
          </Stack>
        </Stack>

        {/* Training Info Cards */}
        <Paper 
          variant="outlined" 
          sx={{ 
            p: { xs: 1.5, sm: 2 }, 
            mb: { xs: 2, md: 2.5 }, 
            borderRadius: { xs: 1.5, sm: 2 },
            backgroundColor: 'background.paper'
          }}
        >
          {/* Vetëm `gap`: `spacing` i Stack-ut u vë margjinë majtas të gjithë elementeve
              përveç të parit, dhe kur rreshti mbështillet, i pari i rreshtit të dytë
              dilte i zhvendosur djathtas — «Data e mbarimit» s'rrinte nën «Trajneri». */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            sx={{ flexWrap: 'wrap', gap: { xs: 1.5, sm: 2 } }}
          >
            <Info label="Trajneri" value={training.trainer} />
            <Info label="Lokacioni" value={training.location} />
            {/* `2026-02-10` është format baze, jo format leximi. */}
            <Info label="Data e fillimit" value={fmtFullDate(training.startDate)} />
            <Info label="Data e mbarimit" value={fmtFullDate(training.endDate)} />
            {/* Financimi rri te çipi lart; këtu ishte i njëjti tekst dy herë. */}
          </Stack>
        </Paper>

        {/* Participants Section Header */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          spacing={{ xs: 1, sm: 1 }}
          mb={{ xs: 1.5, md: 2 }}
        >
          <Typography 
            variant="h6" 
            fontWeight={700}
            sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
          >
            Pjesëmarrësit ({(training.participants || []).length})
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onAddParticipant?.(training.id)}
            sx={{ 
              borderRadius: 2, 
              px: { xs: 2, sm: 1.75 }, 
              textTransform: 'none', 
              fontWeight: 600,
              fontSize: { xs: '0.875rem', sm: '0.875rem' }
            }}
          >
            + Shto pjesëmarrës
          </Button>
        </Stack>

        {/* Participants List - Mobile Cards or Table */}
        {isMobile ? (
          <Box>
            {(training.participants || []).map((p) => (
              <MobileParticipantCard
                key={p.id}
                participant={p}
                training={training}
                onParticipantComplete={onParticipantComplete}
                onEditParticipant={onEditParticipant}
                onSendInvite={onSendInvite}
                inviteSending={inviteSending}
                onAcceptInvite={onAcceptInvite}
              />
            ))}
          </Box>
        ) : (
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              borderColor: 'divider',
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}
          >
            <Box sx={{ overflowX: 'auto' }}>
              <Table
                size="small"
                sx={{
                  '& thead th': {
                    textTransform: 'uppercase',
                    fontSize: { xs: 10, sm: 11, md: 11.5 },
                    letterSpacing: 0.35,
                    color: 'text.secondary',
                    fontWeight: 700,
                    borderBottomColor: 'rgba(0,0,0,0.04)',
                    whiteSpace: 'nowrap',
                  },
                  '& tbody td': {
                    fontSize: { xs: 12, sm: 13, md: 14 },
                    borderBottomColor: 'rgba(0,0,0,0.04)',
                    py: { xs: 1, md: 1.25 },
                  },
                  '& tbody tr:nth-of-type(odd)': { backgroundColor: 'rgba(0,0,0,0.01)' },
                  '& tbody tr:hover': { backgroundColor: 'rgba(124,58,237,0.03)' },
                }}
              >
                <TableHead sx={{ backgroundColor: 'rgba(0,0,0,0.03)' }}>
                  <TableRow>
                    <TableCell>Emri</TableCell>
                    {!isTablet && <TableCell>Email</TableCell>}
                    <TableCell>Departamenti</TableCell>
                    <TableCell>Statusi</TableCell>
                    <TableCell>Ftesa</TableCell>
                    {!isTablet && <TableCell>Përfunduar</TableCell>}
                    {!isTablet && <TableCell>Rezultati</TableCell>}
                    <TableCell align="right">Veprime</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Kokë tabele pa asnjë rresht poshtë: nuk merrej vesh nëse
                      askush s'është shtuar ende apo lista s'u ngarkua. */}
                  {(training.participants || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={isTablet ? 5 : 8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        Ende asnjë pjesëmarrës. Shtoji nga stafi me «Shto pjesëmarrës».
                      </TableCell>
                    </TableRow>
                  )}
                  {(training.participants || []).map((p) => (
                    <TableRow hover key={p.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {p.name}
                        </Typography>
                        {isTablet && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {p.email || 'Email mungon'}
                          </Typography>
                        )}
                      </TableCell>
                      {!isTablet && (
                        <TableCell sx={{ color: p.email ? 'text.primary' : 'text.disabled', fontStyle: p.email ? 'normal' : 'italic' }}>
                          {p.email || 'Email mungon'}
                        </TableCell>
                      )}
                      <TableCell>{p.department}</TableCell>
                      <TableCell>
                        <Chip
                          label={participantStatusLabel(p.status)}
                          size="small"
                          sx={{
                            backgroundColor: (participantStatusStyles[p.status] || participantStatusStyles['Not started']).bg,
                            color: (participantStatusStyles[p.status] || participantStatusStyles['Not started']).color,
                            fontWeight: 700,
                            px: 0.5,
                            fontSize: { xs: '0.7rem', md: '0.75rem' },
                            height: { xs: 20, md: 22 },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={p.inviteStatus === 'Accepted' ? <DoneIcon fontSize="small" /> : undefined}
                          label={p.inviteStatus || 'Pending'}
                          size="small"
                          sx={{
                            backgroundColor:
                              p.inviteStatus === 'Accepted'
                                ? 'rgba(16,185,129,0.12)'
                                : p.inviteStatus === 'Sent'
                                ? 'rgba(59,130,246,0.12)'
                                : 'rgba(248,180,0,0.16)',
                            color:
                              p.inviteStatus === 'Accepted'
                                ? '#3E8FA3'
                                : p.inviteStatus === 'Sent'
                                ? '#2563EB'
                                : '#B45309',
                            fontWeight: 700,
                            px: 0.5,
                            fontSize: { xs: '0.7rem', md: '0.75rem' },
                            height: { xs: 20, md: 22 },
                            '& .MuiChip-icon': { color: 'inherit', fontSize: '0.875rem' },
                          }}
                        />
                      </TableCell>
                      {!isTablet && <TableCell>{p.completionDate || '—'}</TableCell>}
                      {!isTablet && <TableCell>{p.score ? `${p.score}%` : '—'}</TableCell>}
                      <TableCell align="right" sx={{ minWidth: isTablet ? 180 : 260 }}>
                        <Stack spacing={0.5} alignItems="flex-end">
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => onEditParticipant?.(training.id, p)}
                              sx={{ 
                                textTransform: 'none', 
                                px: 0.75, 
                                minWidth: 0, 
                                color: 'text.secondary',
                                fontSize: { xs: '0.75rem', md: '0.875rem' }
                              }}
                            >
                              Edito
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              onClick={() => onParticipantComplete(training.id, p.id)}
                              sx={{ 
                                px: { xs: 0.75, md: 1.2 }, 
                                borderRadius: 1.5,
                                fontSize: { xs: '0.75rem', md: '0.875rem' }
                              }}
                            >
                              ✓ {!isTablet && 'Përfunduar'}
                            </Button>
                          </Stack>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={!isTablet ? <SendIcon fontSize="small" /> : null}
                              onClick={() => onSendInvite?.(training.id, p.id)}
                              disabled={
                                inviteSending?.trainingId === training.id &&
                                inviteSending?.participantId === p.id
                              }
                              sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                px: { xs: 0.75, md: 1.2 },
                                borderRadius: 1.5,
                                fontSize: { xs: '0.7rem', md: '0.875rem' }
                              }}
                            >
                              {isTablet ? (p.inviteStatus === 'Sent' ? 'Ridërgo' : 'Ftesë') : (p.inviteStatus === 'Sent' ? 'Ridërgo' : 'Dërgo ftesë')}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => onAcceptInvite?.(training.id, p.id)}
                              disabled={p.inviteStatus === 'Accepted'}
                              sx={{ 
                                textTransform: 'none', 
                                px: { xs: 0.75, md: 1.2 }, 
                                borderRadius: 1.5,
                                fontSize: { xs: '0.7rem', md: '0.875rem' }
                              }}
                            >
                              Prano
                            </Button>
                          </Stack>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Paper>
        )}
      </Box>
    </Drawer>
  );
}

/** Data e plotë për lexim: `10.02.2026`, jo `2026-02-10`. */
function fmtFullDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt)) return d;
  const dd = String(dt.getDate()).padStart(2, '0');
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  return `${dd}.${mm}.${dt.getFullYear()}`;
}

function Info({ label, value }) {
  return (
    <Box sx={{ minWidth: { xs: '48%', sm: 140, md: 160 }, flex: { xs: '1 1 45%', sm: '0 0 auto' } }}>
      <Typography 
        variant="caption" 
        color="text.secondary" 
        sx={{ 
          textTransform: 'uppercase',
          fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
          fontWeight: 600,
          letterSpacing: 0.5
        }}
      >
        {label}
      </Typography>
      <Typography 
        variant="body2" 
        fontWeight={600}
        sx={{ 
          fontSize: { xs: '0.875rem', sm: '0.875rem', md: '0.875rem' },
          mt: 0.25
        }}
      >
        {/* Pa këtë, «Lokacioni» dilte si etiketë me hapësirë bosh poshtë — dhe
            nuk merrej vesh nëse mungon e dhëna apo faqja s'e ngarkoi. */}
        {value || <Box component="span" sx={{ color: 'text.disabled', fontWeight: 400 }}>—</Box>}
      </Typography>
    </Box>
  );
}
